import { Injectable } from '@angular/core';
import { ResponseContentType } from '@angular/http';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { tap } from 'rxjs/operators/tap';
import { fromEvent } from 'rxjs/observable/fromEvent';

import { ConfigService } from '../config/config.service';

import * as FileSaver from 'file-saver';

@Injectable()
export class DownloadService {
    downloadSubject: Subject<Response> = new Subject<Response>();

    constructor(private http: HttpClient, private configService: ConfigService) {}

    // #region Retrieve Download Service Call

    RetrieveDownload(httpMethod: HttpMethod, ServiceURL: string, Parameters: any, Format: DownloadFormat) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'DELTA',
        });

        let extension: string = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let options = {
            headers: headers,
            responseType: ResponseContentType.Blob,
        };

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod == HttpMethod.GET) {
            // Process request as a GET method
            let queryString = Object.keys(Parameters)
                .reduce((prev: string[], cur: string) => {
                    prev.push(`${encodeURIComponent(cur)}=${encodeURIComponent(Parameters[cur] || '')}`);

                    return prev;
                }, [])
                .join('&');

            let url = this.configService.hostAddress + `${ServiceURL}?${queryString}`;

            responseObs = this.http.get(url, { headers: headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + ServiceURL, Parameters, {
                headers: headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                let blob = resp.body;

                let contentdistribution = resp.headers.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    let matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) filename = matches[1];
                }

                FileSaver.saveAs(blob, filename);

                return resp;
            }),
            catchError((error) => {
                let body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    return error;
                }

                let reader = new FileReader();
                let loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((<string>reader.result).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Retrieve File Service Call

    RetrieveFile(httpMethod: HttpMethod, FileURL: string, Format: DownloadFormat, Filename?: string) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'DELTA',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const url = this.configService.hostAddress + `${FileURL}`;

            responseObs = this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + FileURL, null, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = Filename || 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                if (blob) {
                    FileSaver.saveAs(blob, filename);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion
}

// #region Custom Enumerations

// #region Download Format Enum

export enum DownloadFormat {
    PDF = 0,
    Excel = 1,
    ZIP = 2,
}

// #endregion

// #region Http Method Enum

export enum HttpMethod {
    GET = 0,
    POST = 1,
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/download/download.service.ts